<script lang="ts">
  // libs
  import * as _ from "lodash";

  // stores
  import { userDisabled } from "@retailer/svelte/stores/DwollaIdentityStore";

  // types
  import type { PaymentInstrumentType } from "@shared/types/Invoice";
  import type { AutoPayDistributor } from "@shared/types/AutoPaySettings";

  // props
  export let availablePaymentInstruments: PaymentInstrumentType[];
  export let oldPaymentInstrumentId: number;
  export let oldDistributors: AutoPayDistributor[];
  export let onSave: (
    selectedPaymentInstrumentId: number,
    selectedDistributorIds: number[],
    applCredits: boolean,
  ) => void;
  export let updateShowUnsavedChangesAlert: (newValue: boolean) => void;
  export let oldUseCredits: boolean;
  export let allDistributorsUseDefaultCredits: boolean;

  // data
  const oldDistributorSelections = oldDistributors.map(d => d.is_selected);

  let updatedPaymentInstrumentId = oldPaymentInstrumentId || defaultInstrumentId();
  let updatedDistributors = oldDistributors;
  let useCredits = oldUseCredits == undefined ? false : oldUseCredits;

  // functions
  function defaultInstrumentId() {
    return availablePaymentInstruments.find(instrument => instrument.default_instrument == true)
      ?.id;
  }

  const handleSaveChanges = () => {
    const selectedDistributorIds = updatedDistributors.filter(d => d.is_selected).map(d => d.id);
    onSave(updatedPaymentInstrumentId, selectedDistributorIds, useCredits);
  };

  const toggleDistributor = (distributor: AutoPayDistributor) => {
    const index = updatedDistributors.findIndex(d => d.id == distributor.id);
    updatedDistributors[index] = {
      ...distributor,
      is_selected: !distributor.is_selected,
    };
  };

  const handleUpdatingShowUnsavedChangesAlert = () => {
    const hasPreviousAutoPayPayment = !!oldPaymentInstrumentId;
    const saveIsEnabled = !disableSave;
    const distributorsHaveChanged = !distributorsHaveNotChanged;

    const showUnsavedChangesAlert =
      (hasPreviousAutoPayPayment && saveIsEnabled) || distributorsHaveChanged;
    updateShowUnsavedChangesAlert(showUnsavedChangesAlert);
  };

  const creditApplicationWarning = () => {
    let text =
      "Credits will be applied from the oldest to the newest based on issue date of each credit.";

    if (!allDistributorsUseDefaultCredits) {
      text +=
        " You will not be able to specify which credit applies to a payment unless you schedule a payment";
    }

    return text;
  };

  $: updatedDistributorSelections = updatedDistributors.map(d => d.is_selected);
  $: distributorsHaveNotChanged = _.isEqual(oldDistributorSelections, updatedDistributorSelections);
  $: paymentInstrumentHasNotChanged = oldPaymentInstrumentId == updatedPaymentInstrumentId;
  $: useCreditsHasNotChanged = useCredits == oldUseCredits;
  $: changesMade = !(
    paymentInstrumentHasNotChanged &&
    distributorsHaveNotChanged &&
    useCreditsHasNotChanged
  );
  $: disableSave = !changesMade || $userDisabled;
  $: [disableSave, distributorsHaveNotChanged] && handleUpdatingShowUnsavedChangesAlert();
  $: showSuspensionNotice = updatedDistributors.some(
    d => d.disable_autopay_feature_enabled && d.disabled_by_suspension,
  );
</script>

<section class="fizz-stack-32">
  <h2 class="fizz-heading-2 fizz-stack-8">Auto pay</h2>
  <p class="fizz-text-large fizz-stack-32">
    All changes to auto-pay settings go into effect immediately. Adding a distributor will
    automatically schedule unpaid invoices including those due today, overdue invoices need to be
    paid manually. Removing a distributor will cancel all unpaid auto pay scheduled invoices. If you
    are suspended by either a distributor or by our payment processor, you will have to turn your
    auto pay choices back on once you are no longer suspended.
  </p>

  {#if showSuspensionNotice}
    <div
      class="fizz-alert fizz-alert-warning fizz-stack-32"
      data-testid="autopay-suspension-banner"
    >
      <p class="fizz-text-medium">
        Your AutoPay settings have been turned off due to your suspension. AutoPay must be manually
        turned back on if you want to continue auto-paying your invoices after your suspension has
        been lifted.
      </p>
    </div>
  {/if}

  <div class="fizz-stack-32">
    <p class="fizz-text-medium fizz-full-width fizz-stack-8">
      <strong>Select payment method</strong>
    </p>
    <section>
      <select
        class="fizz-select fizz-full-width fizz-stack-24"
        name="autopay-payment-methods"
        data-testid="autopay-payment-methods-select"
        bind:value={updatedPaymentInstrumentId}
        disabled={$userDisabled}
      >
        <option value="" disabled>Select</option>
        {#each availablePaymentInstruments as paymentInstrument}
          <option
            value={paymentInstrument.id}
            selected={paymentInstrument.id == updatedPaymentInstrumentId}
          >
            {paymentInstrument.nickname}
          </option>
        {/each}
      </select>
    </section>
  </div>

  <section class="fizz-stack-32">
    <p class="fizz-text-medium fizz-full-width fizz-stack-16">
      <strong>Select distributors</strong>
    </p>

    {#each updatedDistributors as distributor}
      <label class="fizz-flex-row-8 fizz-stack-16" for="distributor-{distributor.id}">
        <input
          type="checkbox"
          class="fizz-checkbox"
          id="distributor-{distributor.id}"
          on:click={() => toggleDistributor(distributor)}
          checked={distributor.is_selected}
          disabled={$userDisabled || distributor.currently_suspended}
        />
        <span>{distributor.name}</span>
      </label>
    {/each}
  </section>

  <div class="fizz-stack-32">
    <p class="fizz-text-medium fizz-full-width fizz-stack-8">
      <strong>Apply credits</strong>
    </p>

    <section>
      <label class="fizz-toggle fizz-stack-8" for="apply_credits_toggle">
        <input
          type="checkbox"
          name="apply_credits_toggle"
          id="apply_credits_toggle"
          bind:checked={useCredits}
          disabled={$userDisabled}
        />
        <span class="fizz-toggle-switch" data-testid="apply_credits_toggle" />
        <span>Automatically apply credits to auto pay orders</span>
      </label>
      <p class="fizz-text-subdued">
        {creditApplicationWarning()}
      </p>
    </section>
  </div>

  <button
    data-testid="save-changes"
    class="fizz-button fizz-stack-32"
    disabled={disableSave}
    on:click|preventDefault={handleSaveChanges}
  >
    Save changes
  </button>
</section>
